/**
 * 獎品類型
 *
 * - 1: 寄送獎
 * - 2: 自製優惠卷
 * - 3: 上傳序號
 * - 4: 銘謝惠顧
 * - 5: 會員點數 - 固定給予
 * - 6: 購物金
 * - 7: 獎品代碼
 * - 8: Cyberbiz 動態序號
 * - 9: 會員點數 - 動態計算
 * - 10: 活動點數 - 動態計算
 * - 11: 活動點數 - 固定給予
 * - 12: Shopline 動態序號
 */

const GENERAL_PRIZE = 1;
const GENERAL_COUPON = 2;
const COUPON_CODE = 3;
const THANKYOU = 4;
const POINT = 5;
const CASH_VOUCHER = 6;
const PRIZE_CODE = 7;
const CYBERBIZ_COUPON = 8;
const MEMBERSHIP_DYNAMIC_POINT = 9;
const PROMO_DYNAMIC_POINT = 10;
const PROMO_FIXED_POINT = 11;
const SHOPLINE_COUPON_CODE = 12;

export const prizeTypes = {
    GENERAL_PRIZE,
    GENERAL_COUPON,
    COUPON_CODE,
    THANKYOU,
    POINT,
    CASH_VOUCHER,
    PRIZE_CODE,
    CYBERBIZ_COUPON,
    MEMBERSHIP_DYNAMIC_POINT,
    PROMO_DYNAMIC_POINT,
    PROMO_FIXED_POINT,
    SHOPLINE_COUPON_CODE
};

/**
 * 支援收藏獎品類型
 *
 * - 2: 自製優惠卷
 * - 3: 上傳序號
 */

export const supportCollectPrizeTypes = [GENERAL_COUPON, COUPON_CODE];

/**
 * 獎品兌換期限類型
 *
 * - 0: 不限
 * - 1: 時間區間
 * - 2: 配獎後 N 天內
 * - 3: 配獎後 N 月後月底
 * - 4: 固定到期日
 */

const NO_LIMIT = 0;
const RANGE = 1;
const WITHIN = 2;
const WITHIN_MONTH_LAST_DAY = 3;
const FIXED_EXPIRATION = 4;
export const prizeRedeemDateLimitTypes = {
    NO_LIMIT,
    RANGE,
    WITHIN,
    WITHIN_MONTH_LAST_DAY,
    FIXED_EXPIRATION
};

/**
 * 上傳序號 檔案類型
 *
 * - 1: 序號
 * - 2: 兌換連結
 * - 3: 序號 + 兌換連結
 */

const UPLOAD_COUPON_CODE = 1;
const UPLOAD_URL = 2;
const UPLOAD_CODE_AND_URL = 3;

export const uploadCouponFileTypes = {
    UPLOAD_COUPON_CODE,
    UPLOAD_URL,
    UPLOAD_CODE_AND_URL
};

/**
 * 獎品類型 - 實體獎品
 * - 0: 不顯示
 * - 1: 顯示
 */

const UNLIMITED = 0;
const LIMITED = 1;
export const prizeQuantityLimitedTypes = {
    UNLIMITED,
    LIMITED
};

/**
 * 領獎限制 - 顯示獎品
 * - 0: 不顯示
 * - 1: 顯示
 */

const INVISIBLE = 0;
const VISIBLE = 1;
export const prizeVisibleTypes = {
    INVISIBLE,
    VISIBLE
};

/**
 * 兌換設定 - 實體核銷
 * - 1: 在獎品頁顯示核銷按鈕，由現場人員點擊兌換。 (針對獎品是 一般實體獎 / 自製優惠卷 / 上傳序號時或啟用)
 * - 2: 在獎品頁顯示 QRcode，由實體活動小幫手掃描核銷
 * - 3: 即時監聽電商訂單使用核銷 (針對獎品是 Cyberbiz 優惠券 / Shopline 優惠券 時或啟用)
 */

const REDEEM_BUTTON = 1;
const ON_SITE_ASSISTANT_TOOL = 2;
const ECOMMERCE_ORDER_REDEMPTION_LISTENER = 3;
export const prizeStoreRedeemTypes = {
    REDEEM_BUTTON,
    ON_SITE_ASSISTANT_TOOL,
    ECOMMERCE_ORDER_REDEMPTION_LISTENER
};

// 取得配獎模式名稱
export const prizeGiveWayLabel = Object.entries(prizeTypes).reduce((obj, [name, type]) => {
    obj[type] = name;
    return obj;
}, {});

export function getPrizeTypeLabel(prizeType) {
    const prizeLabel = prizeGiveWayLabel[prizeType];
    return Boolean(prizeLabel) ? prizeLabel : '';
}

// 會員點數
const memberPointPrizeTypes = new Set([prizeTypes.POINT, prizeTypes.MEMBERSHIP_DYNAMIC_POINT]);
export function hasMemberPointPrizeType(prizeType) {
    return memberPointPrizeTypes.has(prizeType);
}

// 活動點數
const promoPointPrizeTypes = new Set([
    prizeTypes.PROMO_DYNAMIC_POINT,
    prizeTypes.PROMO_FIXED_POINT
]);
export function hasPromoPointPrizeType(prizeType) {
    return promoPointPrizeTypes.has(prizeType);
}

// 固定點數
const fixedPointPrizeTypes = new Set([prizeTypes.POINT, prizeTypes.PROMO_FIXED_POINT]);
export function hasFixedPointPrizeType(prizeType) {
    return fixedPointPrizeTypes.has(prizeType);
}

// 動態點數
const dynamicPointPrizeTypes = new Set([
    prizeTypes.MEMBERSHIP_DYNAMIC_POINT,
    prizeTypes.PROMO_DYNAMIC_POINT
]);
export function hasDynamicPointPrizeType(prizeType) {
    return dynamicPointPrizeTypes.has(prizeType);
}

// 點數獎品
const pointPrizeTypes = new Set([
    prizeTypes.MEMBERSHIP_DYNAMIC_POINT,
    prizeTypes.PROMO_DYNAMIC_POINT,
    prizeTypes.POINT,
    prizeTypes.PROMO_FIXED_POINT
]);
export function hasPointPrizeType(prizeType) {
    return pointPrizeTypes.has(prizeType);
}

// 可自動歸戶的獎品
export const carrierClaimPrizeTypesForParticipateWinner = new Set([
    prizeTypes.POINT,
    prizeTypes.CASH_VOUCHER,
    prizeTypes.PRIZE_CODE,
    prizeTypes.CYBERBIZ_COUPON,
    prizeTypes.MEMBERSHIP_DYNAMIC_POINT,
    prizeTypes.SHOPLINE_COUPON_CODE
]);

export function isCarrierClaimPrizeTypesForParticipateWinner(prizeType) {
    return carrierClaimPrizeTypesForParticipateWinner.has(prizeType);
}

// 切換獎品類型需轉換兌換設定時間
const convertRedeemTimePrizeTypes = new Set([prizeTypes.CASH_VOUCHER]);
export function isConvertRedeemTimePrizeType(prizeType) {
    return convertRedeemTimePrizeTypes.has(prizeType) || pointPrizeTypes.has(prizeTypes);
}

// 電商優惠卷類型獎品
export const ecommerceCouponPrizeTypes = new Set([
    prizeTypes.CYBERBIZ_COUPON,
    prizeTypes.SHOPLINE_COUPON_CODE
]);

export function isEcommerceCouponPrizeTypes(prizeType) {
    return ecommerceCouponPrizeTypes.has(prizeType);
}
